import { type ReactNode, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useSubscription } from '@/lib/contexts/SubscriptionContext'
import { useAuth } from '@/lib/contexts/AuthContext'
import { useSiteSafe } from '@/lib/contexts/SiteContext'
import { useWorkspaceSafe } from '@/lib/contexts/WorkspaceContext'
import { getPagesBreakdown } from '@/lib/api/external-api'
import { getPlanById, getPlanDisplayName } from '@/lib/constants/pricing'
import type { FeatureAccess } from '@/lib/types/billing'
import { track } from '@/lib/utils/track'
import { logPaywallShown, usePaywallScope } from '@/lib/observability/paywall'
import { cn } from '@/lib/utils'
import { UpgradeModal } from '@/components/shared/UpgradeModal'

// LP card images — reused from humblytics-lp's "whole conversion stack" section
// for features without a drawn illustration.
import cardAnalytics from '@/assets/paywall/card-analytics.png'
import cardAbTesting from '@/assets/paywall/card-ab-testing.png'
import cardRevenue from '@/assets/paywall/card-revenue.png'

type FeatureKey = keyof FeatureAccess

interface FeatureMeta {
  /** Feature name as it reads in a sentence ("Heatmaps", "A/B tests"). */
  title: string
  /** Plural subject for "{title} are on Business". */
  isPlural: boolean
  description: string
  /** Second sentence after the data lead: "Heatmaps show where they clicked." */
  headlineTail: string
  /** Headline when there's no page data to lead with. */
  fallbackHeadline: string
  planRequired: 'Business'
  imageSrc?: string
  /** Hex color of the image edge — used as the band bg so the card has no seam. */
  bgHex?: string
}

// Copy written for the in-product moment — user is already product-aware (they
// opened the feature). Lead with their own traffic, then the outcome.
// Business is the entry plan every gate points at; Plus is never advertised.
const FEATURE_META: Partial<Record<FeatureKey, FeatureMeta>> = {
  heatmaps: {
    title: 'Heatmaps',
    isPlural: true,
    description: 'See which buttons get clicked, which get ignored, and how far visitors scroll on every page.',
    headlineTail: 'Heatmaps show where they clicked.',
    fallbackHeadline: 'See where your visitors click and how far they scroll.',
    planRequired: 'Business',
  },
  funnels: {
    title: 'Funnels',
    isPlural: true,
    description: 'Build a funnel from the pages you already track and see the one step where most visitors drop off.',
    headlineTail: 'Funnels show where they dropped off.',
    fallbackHeadline: 'Find the step where most of your visitors drop off.',
    planRequired: 'Business',
    imageSrc: cardAnalytics,
    bgHex: '#E7EEFC',
  },
  splitTesting: {
    title: 'A/B tests',
    isPlural: true,
    description: 'Change a headline, button or layout with no code, and pick the winner by revenue, not clicks.',
    headlineTail: 'An A/B test shows which version converts more of them.',
    fallbackHeadline: 'Test a new headline or button and keep the version that sells more.',
    planRequired: 'Business',
    imageSrc: cardAbTesting,
    bgHex: '#FEF2EB',
  },
  aiCopilot: {
    title: 'AI Copilot',
    isPlural: false,
    description: 'Ask about your funnels, visitors and revenue in plain English and get the answer from your own data.',
    headlineTail: 'AI Copilot tells you what to change first.',
    fallbackHeadline: 'Ask your analytics anything in plain English.',
    planRequired: 'Business',
    imageSrc: cardAnalytics,
    bgHex: '#E7EEFC',
  },
  revenueAttribution: {
    title: 'Revenue attribution',
    isPlural: false,
    description: 'Connect Meta and Stripe and see which campaigns pay back, checked against real payments.',
    headlineTail: 'Attribution shows which campaigns brought them.',
    fallbackHeadline: 'See which ads and campaigns actually pay back.',
    planRequired: 'Business',
    imageSrc: cardRevenue,
    bgHex: '#F2FAF5',
  },
  apiAccess: {
    title: 'API access',
    isPlural: false,
    description: 'Generate keys and pull traffic, funnels and revenue into your own dashboards, agents and tools.',
    headlineTail: 'The API puts that data in your own tools.',
    fallbackHeadline: 'Pipe your analytics into any tool or AI agent.',
    planRequired: 'Business',
    imageSrc: cardAnalytics,
    bgHex: '#E7EEFC',
  },
}

const DEFAULT_META: FeatureMeta = {
  title: 'This feature',
  isPlural: false,
  description: 'Upgrade to Business to use it on every site in your workspace.',
  headlineTail: '',
  fallbackHeadline: 'This feature is on Business.',
  planRequired: 'Business',
  imageSrc: cardAnalytics,
  bgHex: '#E7EEFC',
}

export interface GateTopPage {
  /** Unique visitors on the page over the last 7 days. */
  people: number
  /** "acme.com/pricing" */
  label: string
  /** Average scroll depth on that page, 0–100. */
  scrollPercent: number | null
}

/**
 * Headline for a gated feature: the user's own top page when we have it
 * ("1,530 people saw acme.com/pricing last week. Heatmaps show where they
 * clicked."), otherwise a plain outcome line.
 */
export function buildGateHeadline(meta: Pick<FeatureMeta, 'headlineTail' | 'fallbackHeadline'>, top: GateTopPage | null): string {
  if (!top || top.people < 1 || !meta.headlineTail) return meta.fallbackHeadline
  const who = top.people === 1 ? '1 person' : `${top.people.toLocaleString('en-US')} people`
  return `${who} saw ${top.label} last week. ${meta.headlineTail}`
}

/** "acme.com" + "/pricing" → "acme.com/pricing"; the home page is just the domain. */
export function formatPageLabel(domain: string | null | undefined, path: string): string {
  const host = (domain ?? '').replace(/^https?:\/\//, '').replace(/\/+$/, '')
  const p = path === '/' ? '' : path.startsWith('/') ? path : `/${path}`
  return host ? `${host}${p}` : p || '/'
}

/** Top page of the current site over the last 7 days, by total views. */
function useGateTopPage(enabled: boolean): GateTopPage | null {
  const { token } = useAuth()
  const site = useSiteSafe()
  const workspace = useWorkspaceSafe()
  const propertyId = workspace?.selectedPropertyId || site?.currentSite?.propertyId || site?.id || null
  const domain = site?.currentSite?.domain ?? null

  const { data } = useQuery({
    queryKey: ['feature-gate-top-page', propertyId],
    queryFn: () => getPagesBreakdown('day_PAST_7_D', propertyId!, token!, 5),
    enabled: enabled && !!token && !!propertyId,
    staleTime: 10 * 60 * 1000,
    retry: false,
  })

  const rows = data?.data ?? []
  if (rows.length === 0) return null
  const top = rows.reduce((a, b) => (b.page_views > a.page_views ? b : a))
  return {
    people: top.unique_visitors ?? 0,
    label: formatPageLabel(domain, top.page),
    scrollPercent: Number.isFinite(top.avg_scroll_percent) ? Math.round(top.avg_scroll_percent) : null,
  }
}

interface FeatureGateProps {
  feature: FeatureKey
  children: ReactNode
}

/**
 * Wraps content that requires a specific feature flag.
 * Shows the upgrade gate when the user's plan doesn't include the feature.
 * Renders children normally when access is granted or subscription is still loading.
 */
export function FeatureGate({ feature, children }: FeatureGateProps) {
  const { hasFeature, isLoading, usedTrial, currentPlan, planName } = useSubscription()
  const site = useSiteSafe()
  const [modalOpen, setModalOpen] = useState(false)
  const { token } = useAuth()
  const location = useLocation()
  const paywallScope = usePaywallScope()
  const isShareMode = location.pathname.startsWith('/s/')

  const isDenied = !isShareMode && !isLoading && !hasFeature(feature)
  const topPage = useGateTopPage(isDenied)
  const prevDenied = useRef(false)
  useEffect(() => {
    if (isDenied && !prevDenied.current) {
      const meta = FEATURE_META[feature]
      track('feature_access_denied', token, { feature, plan_tier: meta?.planRequired ?? 'Business' })
      logPaywallShown({
        surface: 'feature_gate',
        trigger: 'feature_denied',
        featureKey: feature,
        pathname: location.pathname,
        scope: paywallScope,
      })
    }
    prevDenied.current = isDenied
  }, [isDenied, feature, token, location.pathname, paywallScope])

  // Share links bypass the gate — the owner already has access
  if (!isDenied) {
    return <>{children}</>
  }

  const meta = FEATURE_META[feature] ?? DEFAULT_META
  const plan = getPlanById('business')
  const price = `$${plan?.monthlyPrice ?? 79}/mo`
  const currentName = getPlanDisplayName(currentPlan, planName)
  const covers =
    currentPlan === 'free'
      ? 'Your Free plan covers traffic, pages, clicks and forms.'
      : `Your ${currentName} plan doesn't include ${meta.isPlural ? 'them' : 'it'}.`

  const modalFeature = UPGRADE_MODAL_FEATURE[feature] ?? meta.title
  const openModal = () => {
    track('upgrade_modal_shown', token, { trigger_reason: 'feature_gate', feature: modalFeature })
    setModalOpen(true)
  }

  return (
    <>
    <FeatureGateView
      headline={buildGateHeadline(meta, topPage)}
      subline={`${meta.title} ${meta.isPlural ? 'are' : 'is'} on ${meta.planRequired}. ${covers}`}
      title={`Unlock ${meta.title === 'This feature' ? 'this feature' : meta.title}`}
      description={meta.description}
      planRequired={meta.planRequired}
      ctaLabel={usedTrial ? `Upgrade to ${meta.planRequired}` : `Start 14-day ${meta.planRequired} trial`}
      priceAnchor={usedTrial ? `From ${price} · cancel anytime` : `Then ${price} · cancel anytime`}
      onUpgrade={openModal}
      illustration={
        feature === 'heatmaps' ? (
          <HeatmapGateIllustration
            pageLabel={topPage?.label ?? site?.currentSite?.domain ?? null}
            scrollLabel={topPage?.scrollPercent != null ? `${topPage.scrollPercent}% avg scroll depth` : null}
          />
        ) : (
          <ImageIllustration src={meta.imageSrc ?? cardAnalytics} bgHex={meta.bgHex ?? '#E7EEFC'} alt={meta.title} />
        )
      }
    />
    {/* Local instance so the gate works on any route, not only inside DashboardLayout. */}
    <UpgradeModal open={modalOpen} onOpenChange={setModalOpen} featureName={modalFeature} />
    </>
  )
}

/** `UpgradeModal` feature names for each gate. */
const UPGRADE_MODAL_FEATURE: Partial<Record<FeatureKey, string>> = {
  heatmaps: 'Heatmaps',
  funnels: 'Funnels',
  splitTesting: 'A/B Testing',
  aiCopilot: 'AI Copilot',
  revenueAttribution: 'Revenue attribution',
  apiAccess: 'API access',
}

export interface FeatureGateViewProps {
  headline: string
  subline: string
  title: string
  description: string
  planRequired: string
  ctaLabel: string
  priceAnchor: string
  onUpgrade: () => void
  illustration: ReactNode
}

/**
 * Feature gate (Paper "Heatmaps · Feature gate (Free)"): the user's own data
 * as the page title, then one raised card with the feature drawn on the left
 * and the single orange CTA on the right. Presentational.
 */
export function FeatureGateView({
  headline,
  subline,
  title,
  description,
  planRequired,
  ctaLabel,
  priceAnchor,
  onUpgrade,
  illustration,
}: FeatureGateViewProps) {
  return (
    <div className="flex flex-col gap-4 px-4 pb-10 pt-6 sm:px-9">
      <div className="flex flex-col gap-1.5">
        <h1 className="text-[22px] font-semibold leading-7 tracking-[-0.02em] text-foreground">{headline}</h1>
        <p className="text-[13px] leading-4 text-muted-foreground">{subline}</p>
      </div>
      <div className="well">
        <div className="surface-raised flex flex-col gap-4 p-4 lg:flex-row">
          {illustration}
          <div className="flex min-w-0 flex-1 flex-col justify-center px-1 py-2 lg:pl-4 lg:pr-5">
            {/* Plan chip doubles as the "see plans" affordance */}
            <button
              type="button"
              onClick={onUpgrade}
              className="inline-flex h-6 items-center gap-1.5 self-start rounded-full bg-canvas px-[9px] text-xs font-medium leading-4 text-foreground shadow-[0_0_0_1px_rgb(0_0_0/0.06)] transition-colors hover:bg-muted"
              aria-label={`View ${planRequired} plan and higher`}
            >
              {planRequired} plan or higher
              <svg width="10" height="10" viewBox="0 0 24 24" aria-hidden>
                <path d="m9 18 6-6-6-6" fill="none" stroke="currentColor" strokeOpacity="0.45" strokeWidth="2.6" strokeLinecap="round" />
              </svg>
            </button>
            <h2 className="mt-4 text-[22px] font-semibold leading-7 tracking-[-0.02em] text-foreground">{title}</h2>
            <p className="mt-2 text-sm leading-5 text-muted-foreground">{description}</p>
            <div className="mt-6 flex">
              <button
                type="button"
                onClick={onUpgrade}
                className="inline-flex h-8 items-center rounded-lg bg-primary px-3.5 text-[13px] font-medium leading-4 text-primary-foreground transition-colors hover:bg-primary/90"
              >
                {ctaLabel}
              </button>
            </div>
            <p className="mt-2 text-xs leading-4 text-muted-foreground">{priceAnchor}</p>
          </div>
        </div>
      </div>
    </div>
  )
}

function ImageIllustration({ src, bgHex, alt }: { src: string; bgHex: string; alt: string }) {
  return (
    <div
      className="flex aspect-[3/2] w-full shrink-0 items-center justify-center overflow-hidden rounded-xl p-3 lg:aspect-auto lg:w-[640px]"
      style={{ backgroundColor: bgHex }}
    >
      <img src={src} alt={alt} className="block h-auto max-h-full w-auto max-w-full object-contain" loading="lazy" />
    </div>
  )
}

/** Heat blobs in the 640px frame: [left, top, outer, middle, core] (Paper gate artboard). */
const HEAT_SPOTS: Array<[number, number, number, number, number]> = [
  [280, 200, 78, 48, 20],
  [112, 222, 40, 24, 10],
  [295, 57, 50, 30, 12],
  [490, 229, 30, 18, 8],
]

/**
 * Drawn heatmap of a pricing-style page (Paper gate artboard). Decorative: the
 * address bar shows the user's real top page when known, and the scroll pill
 * appears only with a real average scroll depth.
 */
export function HeatmapGateIllustration({
  pageLabel,
  scrollLabel,
}: {
  pageLabel: string | null
  scrollLabel: string | null
}) {
  return (
    <div
      aria-hidden
      className="relative flex w-full shrink-0 flex-col overflow-hidden rounded-xl bg-neutral-50 px-5 pt-5 shadow-[0_0_0_1px_rgb(0_0_0/0.043)] lg:w-[640px] dark:bg-white/[0.03]"
    >
      <div className="flex min-w-[560px] flex-1 flex-col rounded-t-[10px] bg-card shadow-[0_0_0_1px_rgb(0_0_0/0.07)]">
        <div className="flex h-8 shrink-0 items-center gap-1.5 border-b border-black/5 px-3">
          <span className="size-2 rounded-full bg-neutral-200" />
          <span className="size-2 rounded-full bg-neutral-200" />
          <span className="size-2 rounded-full bg-neutral-200" />
          <span className={cn('ml-2.5 flex h-5 items-center rounded-md bg-canvas px-2.5 text-xs leading-4 text-muted-foreground', !pageLabel && 'w-40')}>
            {pageLabel}
          </span>
        </div>
        <div className="flex flex-col items-center gap-2.5 px-6 pb-7 pt-[22px]">
          <span className="h-3.5 w-[220px] rounded bg-neutral-200/90" />
          <span className="h-[7px] w-[300px] rounded bg-neutral-100" />
          <div className="mt-3 flex w-full gap-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex flex-1 flex-col gap-2 rounded-lg bg-card p-3.5 shadow-[0_0_0_1px_rgb(0_0_0/0.063)]">
                <span className="h-2 w-1/2 rounded bg-neutral-100" />
                <span className="h-3.5 w-[70%] rounded bg-neutral-200/90" />
                <span className="h-1.5 w-[90%] rounded bg-neutral-100" />
                <span className="h-1.5 w-[80%] rounded bg-neutral-100" />
                <span className="h-1.5 w-[85%] rounded bg-neutral-100" />
                <span className={cn('mt-1.5 h-[26px] rounded-md', i === 1 ? 'bg-neutral-300' : 'bg-neutral-100')} />
              </div>
            ))}
          </div>
        </div>
      </div>
      {HEAT_SPOTS.map(([left, top, size, mid, core]) => {
        return (
          <span key={`${left}-${top}`}>
            <span className="absolute rounded-full bg-chart-1 opacity-[0.22]" style={{ left, top, width: size, height: size }} />
            <span
              className="absolute rounded-full bg-chart-2 opacity-[0.42]"
              style={{ left: left + (size - mid) / 2, top: top + (size - mid) / 2, width: mid, height: mid }}
            />
            <span
              className="absolute rounded-full bg-chart-3 opacity-[0.85]"
              style={{ left: left + (size - core) / 2, top: top + (size - core) / 2, width: core, height: core }}
            />
          </span>
        )
      })}
      <div className="absolute inset-x-5 top-[146px] flex items-center gap-2">
        <span className="h-0 flex-1 border-t border-dashed border-black/30" />
        {scrollLabel && (
          <span className="rounded-full bg-neutral-900 px-2 py-[3px] text-xs font-medium leading-4 text-white">
            {scrollLabel}
          </span>
        )}
      </div>
    </div>
  )
}

export default FeatureGate
